import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { BRAND } from "~/brand";
import Logo from "~/components/Logo";
import { PageTransition } from "~/components/PageTransition";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/components/ui/input-otp";
import { partnerOnboardingMutationOptions, profileQueryOptions } from "~/lib/api";
import { authClient } from "~/lib/auth";
import { isKnownPartner, type PartnerKey } from "~/lib/partners";
import { sanitizeInternalPath } from "~/lib/safe-redirect";
import { track } from "~/lib/track";
import { queryClient } from "~/router";

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: "de code bestaat uit 6 cijfers",
  }),
});

const normalizeVerifyErrorMessage = (message?: string, partner?: string) => {
  console.error("MESSAGE:", message);
  if (!message) {
    return "De code is ongeldig of verlopen. Vraag een nieuwe code aan en probeer het opnieuw.";
  }

  const normalized = message.toLowerCase();
  if (normalized.includes("failed to fetch") || normalized.includes("networkerror")) {
    return "We konden geen verbinding maken. Controleer je internetverbinding en probeer het opnieuw.";
  }
  if (
    normalized.includes("invalid") ||
    normalized.includes("otp") ||
    normalized.includes("code")
  ) {
    return "De code is ongeldig of verlopen. Vraag een nieuwe code aan en probeer het opnieuw.";
  }
  if (normalized.includes("too many requests")) {
    return "Je hebt te vaak geprobeerd in te loggen. Wacht even en probeer het opnieuw.";
  }
  if (normalized.includes("we konden de gevraagde gegevens niet vinden.")) {
    console.error(`Referral not found for partner: ${partner}`);
    return `Oeps, er is iets misgegaan tijdens het koppelen met ${partner}. Als dit probleem zich blijft voordoen, neem dan contact op.`;
  }

  return "Inloggen is niet gelukt. Probeer het opnieuw.";
};

/**
 * The `?partner=` on the verify link, or undefined when it names no partner we
 * know. Only a recognised partner claims the referral after the OTP, so this
 * checks against every known partner rather than a hardcoded pair.
 */
const getSupportedPartner = (value: string | null): PartnerKey | undefined =>
  isKnownPartner(value) ? value : undefined;

export const Route = createFileRoute("/(auth-pages)/verifieren")({
  loader: async ({ location }) => {
    const params = new URLSearchParams(location.search);
    const referralId = params.get("referralId");
    const partner = getSupportedPartner(params.get("partner"));
    const email = params.get("email");
    const from = params.get("from") || "/inloggen";
    // Post-login destination, e.g. the mandate step a reminder link fell back
    // from. Same-origin paths only; anything else is dropped here.
    const redirect = sanitizeInternalPath(params.get("redirect"));
    return { email, from, referralId, partner, redirect };
  },
  component: VerifyForm,
});

function VerifyForm() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { email, from, referralId, partner, redirect } = Route.useLoaderData();

  const { mutateAsync: partnerOnboarding } = useMutation(
    partnerOnboardingMutationOptions(),
  );
  const isSignUp = from === "/aanmelden" || from === "/aanmelden-partner";

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: "",
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    setErrorMessage("");

    const pin = data.pin;
    if (!isSignUp) track("login_otp_submitted");

    try {
      console.info(`Attempting OTP validation and sign-in for code [${pin}]`);
      if (email) {
        const result = await authClient.signIn.emailOtp({
          email,
          otp: pin,
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!isSignUp) track("login_completed");

        // The identity just switched from the (possibly anonymous) session to
        // the real user. Drop all cached query data so the resume resolution
        // below reads this user's chargers/accounts rather than stale data
        // left over from an anonymous browsing session.
        queryClient.clear();
      } else if (partner && referralId) {
        throw new Error("Partner email ontbreekt");
      }

      const session = await authClient.getSession();

      // This will get the associated order and prefill some profile information based on that.
      let provider: string | null = null;
      if (partner && referralId) {
        ({ provider } = await partnerOnboarding({ referralId }));
      }

      const isOnboarded = session.data?.user?.isOnboarded;
      if (partner) {
        await queryClient.invalidateQueries({ queryKey: ["profile", "current"] });
        await queryClient.ensureQueryData(profileQueryOptions());
      }

      if (!isOnboarded) {
        await navigate({
          to: "/onboarding/$step",
          params: { step: "provider-selection" },
          search: provider ? { provider } : undefined,
        });
        return;
      }

      // Onboarded customers go where they were headed; onboarding always wins
      // above, so an unfinished account never skips it via a deep link.
      if (redirect) {
        await navigate({ href: redirect });
        return;
      }

      await navigate({ to: "/dashboard" });
    } catch (error) {
      console.error(error);
      if (!isSignUp) {
        track("login_failed", {
          stage: "verify_otp",
          error: (error as Error)?.message,
        });
      }
      setErrorMessage(normalizeVerifyErrorMessage((error as Error)?.message, partner));
    }
  }

  return (
    <div className="bg-background relative flex items-center justify-center pt-8 lg:min-h-screen lg:pt-0">
      <div className="flex w-full max-w-md flex-col gap-6 px-4">
        <PageTransition delay={0}>
          <div className="flex flex-col gap-4">
            <Logo className="-ml-3 lg:size-14" />
            <h1 className="text-2xl font-medium lg:text-3xl">Check je mail</h1>
            <p>
              We hebben een code gestuurd naar{" "}
              <span className="text-foreground/70">{email}</span>
            </p>
          </div>
        </PageTransition>

        <PageTransition delay={100}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="pin"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        aria-label="Verificatiecode"
                        onComplete={form.handleSubmit(onSubmit)}
                        {...field}
                      >
                        <InputOTPGroup className="flex w-full flex-row gap-3">
                          <InputOTPSlot
                            index={0}
                            className="h-16 w-full rounded-md border bg-white text-lg font-semibold"
                          />
                          <InputOTPSlot
                            index={1}
                            className="h-16 w-full rounded-md border bg-white text-lg font-semibold"
                          />
                          <InputOTPSlot
                            index={2}
                            className="h-16 w-full rounded-md border bg-white text-lg font-semibold"
                          />
                          <InputOTPSlot
                            index={3}
                            className="h-16 w-full rounded-md border bg-white text-lg font-semibold"
                          />
                          <InputOTPSlot
                            index={4}
                            className="h-16 w-full rounded-md border bg-white text-lg font-semibold"
                          />
                          <InputOTPSlot
                            index={5}
                            className="h-16 w-full rounded-md border bg-white text-lg font-semibold"
                          />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Versturen...
                  </>
                ) : isSignUp ? (
                  "Account aanmaken"
                ) : (
                  "Inloggen"
                )}
              </Button>

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}
            </form>
          </Form>
        </PageTransition>

        <PageTransition delay={200}>
          <div className="flex flex-col items-center">
            <Link
              to={from}
              search={
                partner && referralId
                  ? { referralId, partner }
                  : redirect
                    ? { redirect }
                    : undefined
              }
            >
              <Button variant="link">
                {isSignUp
                  ? "Aanmelden met een andere email"
                  : "Inloggen met een andere email"}
              </Button>
            </Link>
          </div>
        </PageTransition>
      </div>
      {BRAND.cornerPattern && (
        <img
          src="/corner-pattern.svg"
          className="invisible absolute bottom-0 left-0 opacity-50 lg:visible"
          alt=""
        />
      )}
    </div>
  );
}
